/**
 * Width Calculations - Pure Utility Functions
 *
 * Functions for calculating and distributing container widths.
 * Widths are stored as percentage strings (e.g., "33%", "50%", "100%").
 *
 * Width Distribution Rules:
 * - 1 container in row: 100%
 * - 2 containers in row: 50% each
 * - 3 containers in row: 33% each (or 34%/33%/33% for even split)
 */

import type { ContainerAPI } from '../../types/api.types';
import type { WidthUpdate, WidthDistributionStrategy } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS, WIDTH_PRESETS } from '../constants/constraints';

// ============================================================================
// Width Parsing & Formatting
// ============================================================================

/**
 * Parses a width string to a numeric percentage value
 *
 * @param width - Width string (e.g., "33%", "100%", "50")
 * @returns Numeric percentage value
 *
 * @example
 * parseWidth("33%") // => 33
 * parseWidth("100%") // => 100
 * parseWidth("50") // => 50
 */
export const parseWidth = (width: string): number => {
  const trimmed = width.trim();
  const numericValue = parseFloat(trimmed.replace('%', ''));
  return isNaN(numericValue) ? 100 : numericValue;
};

/**
 * Formats a numeric percentage as a width string
 *
 * @param percent - Numeric percentage (e.g., 33, 50, 100)
 * @returns Formatted width string with % suffix
 *
 * @example
 * formatWidth(33) // => "33%"
 * formatWidth(100) // => "100%"
 */
export const formatWidth = (percent: number): string => {
  // Round to avoid floating point issues
  const rounded = Math.round(percent);
  return `${rounded}%`;
};

// ============================================================================
// Width Distribution Functions
// ============================================================================

/**
 * Calculates equal width for a given number of containers
 *
 * @param count - Number of containers in the row
 * @returns Width string for equal distribution
 *
 * @example
 * distributeWidthsEqually(1) // => "100%"
 * distributeWidthsEqually(2) // => "50%"
 * distributeWidthsEqually(3) // => "33%"
 */
export const distributeWidthsEqually = (count: number): string => {
  if (count <= 0) return WIDTH_PRESETS.SINGLE;
  if (count === 1) return WIDTH_PRESETS.SINGLE;
  if (count === 2) return WIDTH_PRESETS.HALF;
  if (count === 3) return WIDTH_PRESETS.THIRD;

  // For more than 3 (shouldn't happen with constraints, but handle gracefully)
  return formatWidth(Math.floor(100 / count));
};

/**
 * Gets widths for all containers in a row for equal distribution
 *
 * @param count - Number of containers in the row
 * @returns Array of width strings
 *
 * @example
 * getEqualWidths(3) // => ["34%", "33%", "33%"]
 */
export const getEqualWidths = (count: number): string[] => {
  if (count <= 0) return [];
  if (count === 1) return ['100%'];
  if (count === 2) return ['50%', '50%'];

  // For 3 containers: 34% + 33% + 33% = 100%
  if (count === 3) {
    return ['34%', '33%', '33%'];
  }

  // For more than 3 (shouldn't happen with constraints)
  const baseWidth = Math.floor(100 / count);
  const remainder = 100 - baseWidth * count;
  const widths = Array(count).fill(formatWidth(baseWidth));

  // Distribute remainder to first containers
  for (let i = 0; i < remainder; i++) {
    widths[i] = formatWidth(baseWidth + 1);
  }

  return widths;
};

/**
 * Calculates width updates when adding a container to a row
 *
 * @param existingContainers - Containers already in the row
 * @param newContainerId - ID of the new container being added
 * @param strategy - Width distribution strategy (default: 'equal')
 * @returns Map of container ID to new width string
 *
 * @example
 * calculateWidthsOnAdd([container1, container2], 'new-id')
 * // => Map { 'container1' => '33%', 'container2' => '33%', 'new-id' => '34%' }
 */
export const calculateWidthsOnAdd = (
  existingContainers: ContainerAPI[],
  newContainerId: string,
  strategy: WidthDistributionStrategy = 'equal'
): Map<string, string> => {
  const result = new Map<string, string>();
  const newCount = existingContainers.length + 1;

  if (strategy === 'equal') {
    const widths = getEqualWidths(newCount);

    // Sort existing by order
    const sorted = [...existingContainers].sort((a, b) => a.order - b.order);

    // Assign widths to existing containers
    sorted.forEach((container, index) => {
      result.set(container.id, widths[index]);
    });

    // New container gets the last width
    result.set(newContainerId, widths[newCount - 1]);
  } else if (strategy === 'preserve') {
    // Calculate used width
    const usedWidth = existingContainers.reduce((sum, c) => {
      return sum + parseWidth(c.configuration?.width || '100%');
    }, 0);

    // New container gets remainder
    const remainingWidth = Math.max(CONTAINER_CONSTRAINTS.MIN_WIDTH_PERCENT, 100 - usedWidth);
    result.set(newContainerId, formatWidth(remainingWidth));

    // Scale existing if needed to make room
    if (usedWidth >= 100) {
      const scaleFactor = (100 - CONTAINER_CONSTRAINTS.MIN_WIDTH_PERCENT) / usedWidth;
      existingContainers.forEach((c) => {
        const currentWidth = parseWidth(c.configuration?.width || '100%');
        result.set(c.id, formatWidth(currentWidth * scaleFactor));
      });
    }
  } else if (strategy === 'expand-new') {
    // New container takes remaining space
    const usedWidth = existingContainers.reduce((sum, c) => {
      return sum + parseWidth(c.configuration?.width || '100%');
    }, 0);

    const newWidth = Math.max(CONTAINER_CONSTRAINTS.MIN_WIDTH_PERCENT, 100 - usedWidth);
    result.set(newContainerId, formatWidth(newWidth));
  }

  return result;
};

/**
 * Calculates width updates when removing a container from a row
 *
 * @param containers - All containers in the row (including the one being removed)
 * @param removedId - ID of the container being removed
 * @param strategy - Width distribution strategy (default: 'equal')
 * @returns Map of remaining container IDs to new width strings
 *
 * @example
 * calculateWidthsOnRemove([c1, c2, c3], 'c2')
 * // => Map { 'c1' => '50%', 'c3' => '50%' }
 */
export const calculateWidthsOnRemove = (
  containers: ContainerAPI[],
  removedId: string,
  strategy: WidthDistributionStrategy = 'equal'
): Map<string, string> => {
  const result = new Map<string, string>();
  const remaining = containers.filter((c) => c.id !== removedId);

  if (remaining.length === 0) {
    return result;
  }

  if (strategy === 'equal') {
    const widths = getEqualWidths(remaining.length);
    const sorted = [...remaining].sort((a, b) => a.order - b.order);

    sorted.forEach((container, index) => {
      result.set(container.id, widths[index]);
    });
  } else if (strategy === 'preserve' || strategy === 'expand-new') {
    // Find the removed container's width
    const removedContainer = containers.find((c) => c.id === removedId);
    const freedWidth = removedContainer
      ? parseWidth(removedContainer.configuration?.width || '0%')
      : 0;

    // Distribute freed width proportionally
    const totalRemainingWidth = remaining.reduce((sum, c) => {
      return sum + parseWidth(c.configuration?.width || '100%');
    }, 0);

    remaining.forEach((c) => {
      const currentWidth = parseWidth(c.configuration?.width || '100%');
      const proportion = currentWidth / totalRemainingWidth;
      const additionalWidth = freedWidth * proportion;
      result.set(c.id, formatWidth(currentWidth + additionalWidth));
    });
  }

  return result;
};

/**
 * Creates a WidthUpdate array from a width map
 *
 * @param containers - Containers to get previous widths from
 * @param newWidths - Map of new widths
 * @returns Array of WidthUpdate objects
 */
export const createWidthUpdates = (
  containers: ContainerAPI[],
  newWidths: Map<string, string>
): WidthUpdate[] => {
  const updates: WidthUpdate[] = [];

  newWidths.forEach((newWidth, containerId) => {
    const container = containers.find((c) => c.id === containerId);
    const previousWidth = container?.configuration?.width || '100%';

    if (previousWidth !== newWidth) {
      updates.push({
        containerId,
        newWidth,
        previousWidth,
      });
    }
  });

  return updates;
};

// ============================================================================
// Width Validation Functions
// ============================================================================

/**
 * Validates if a width value is within acceptable bounds
 *
 * @param width - Width string to validate
 * @returns Whether the width is valid
 */
export const isValidWidth = (width: string): boolean => {
  const numeric = parseWidth(width);
  return (
    numeric >= CONTAINER_CONSTRAINTS.MIN_WIDTH_PERCENT &&
    numeric <= CONTAINER_CONSTRAINTS.MAX_WIDTH_PERCENT
  );
};

/**
 * Calculates total width of containers
 *
 * @param containers - Containers to sum
 * @returns Total width percentage
 */
export const getTotalWidth = (containers: ContainerAPI[]): number => {
  return containers.reduce((sum, c) => {
    return sum + parseWidth(c.configuration?.width || '100%');
  }, 0);
};

/**
 * Checks if containers fit within 100% width
 *
 * @param containers - Containers to check
 * @param tolerance - Acceptable overflow percentage (default: 1%)
 * @returns Whether containers fit
 */
export const containersFitWidth = (
  containers: ContainerAPI[],
  tolerance: number = 1
): boolean => {
  const total = getTotalWidth(containers);
  return total <= 100 + tolerance;
};

// ============================================================================
// Lock Validation Functions
// ============================================================================

/**
 * Validation result for lock impact check
 */
export interface LockValidationResult {
  /** Whether the lock operation can proceed */
  canLock: boolean;
  /** Reason why the lock cannot proceed (if canLock is false) */
  reason?: string;
}

/**
 * Container width data for lock validation
 */
export interface ContainerWidthData {
  /** Width percentage of the container */
  width: number;
  /** Whether the container is currently locked */
  isLocked: boolean;
}

/**
 * Check if locking a container would cause invalid width distribution
 *
 * This prevents scenarios where locking a container would force other
 * unlocked containers below the minimum width threshold.
 *
 * @param containerWidth - Width of container to lock
 * @param otherContainers - Other containers in the same row
 * @returns Validation result with canLock status and optional reason
 *
 * @example
 * // Row with 3 containers: 34%, 33%, 33%
 * // First two already locked, trying to lock third
 * validateLockImpact(33, [
 *   { width: 34, isLocked: true },
 *   { width: 33, isLocked: true }
 * ])
 * // => { canLock: true } (no unlocked containers to affect)
 *
 * @example
 * // Row with 3 containers: 60%, 25%, 15%
 * // Trying to lock 60%, would force 25% to absorb from 15%
 * validateLockImpact(60, [
 *   { width: 25, isLocked: true },
 *   { width: 15, isLocked: false }
 * ])
 * // => { canLock: true } (15% unlocked, but gets remaining 15%)
 */
export const validateLockImpact = (
  containerWidth: number,
  otherContainers: ContainerWidthData[]
): LockValidationResult => {
  const { MIN_WIDTH_PERCENT } = CONTAINER_CONSTRAINTS;

  // Calculate total locked width (including the one being locked)
  const currentLockedWidth = otherContainers
    .filter((c) => c.isLocked)
    .reduce((sum, c) => sum + c.width, 0);

  const totalLockedAfter = currentLockedWidth + containerWidth;

  // Check if remaining space can accommodate unlocked containers
  const unlockedCount = otherContainers.filter((c) => !c.isLocked).length;
  const remainingWidth = 100 - totalLockedAfter;
  const minRequiredForUnlocked = unlockedCount * MIN_WIDTH_PERCENT;

  if (remainingWidth < minRequiredForUnlocked) {
    return {
      canLock: false,
      reason: `Cannot lock: would force ${unlockedCount} container(s) to share only ${remainingWidth}% (minimum ${minRequiredForUnlocked}% needed)`,
    };
  }

  return { canLock: true };
};
